import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { clientsApi } from '../api/api';

const EMPTY = { client_code:'', name:'', contact_person:'', email:'', phone:'', address:'', city:'', state:'', gstin:'', credit_limit:'', status:'active' };
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];

export default function ClientMaster() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients', search, statusFilter],
    queryFn: () => clientsApi.getAll({ search, status: statusFilter }),
  });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? clientsApi.update(editing.id, d) : clientsApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['clients']); qc.invalidateQueries(['dashboard']); toast.success(editing ? 'Client updated' : 'Client created'); closeModal(); },
    onError: (e) => toast.error(e.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: clientsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['clients']); toast.success('Client deleted'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({...c}); setModal(true); };
  const closeModal = () => { setModal(false); setEditing(null); };
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = (e) => { e.preventDefault(); saveMutation.mutate(form); };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Client Master</span>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Client</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <Users size={15} />
            <span className="card-title">Clients ({clients.length})</span>
            <div className="search-wrap">
              <Search /><input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr>
                  <th>Code</th><th>Name</th><th>Contact</th><th>Phone</th><th>City</th><th>State</th><th>GSTIN</th><th>Credit Limit</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {clients.length === 0 && <tr><td colSpan={10}><div className="empty-state"><Users /><p>No clients found</p></div></td></tr>}
                  {clients.map(c => (
                    <tr key={c.id}>
                      <td className="mono">{c.client_code}</td>
                      <td style={{fontWeight:500}}>{c.name}</td>
                      <td>{c.contact_person || '—'}</td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.city || '—'}</td>
                      <td>{c.state || '—'}</td>
                      <td className="mono">{c.gstin || '—'}</td>
                      <td className="amount">₹{Number(c.credit_limit||0).toLocaleString('en-IN')}</td>
                      <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-icon" onClick={() => openEdit(c)}><Pencil /></button>
                          {/* <button className="btn-icon danger" onClick={() => { if(window.confirm('Delete this client?')) deleteMutation.mutate(c.id); }}><Trash2 /></button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="btn btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Client Code *</label>
                    <input className="form-control" required value={form.client_code} onChange={e => set('client_code', e.target.value)} placeholder="e.g. CLT-001" disabled={!!editing} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company Name *</label>
                    <input className="form-control" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Client company name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Person</label>
                    <input className="form-control" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GSTIN</label>
                    <input className="form-control" value={form.gstin} onChange={e => set('gstin', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <select className="form-control" value={form.state} onChange={e => set('state', e.target.value)}>
                      <option value="">Select state</option>
                      {STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Credit Limit (₹)</label>
                    <input className="form-control" type="number" min="0" value={form.credit_limit} onChange={e => set('credit_limit', e.target.value)} placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group form-full">
                    <label className="form-label">Address</label>
                    <textarea className="form-control" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full address" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : editing ? 'Update Client' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}